import React, { useCallback, useContext, useEffect } from "react";
import { graphql, useFragment } from "react-relay";

import { faCalendarDay } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCog, faEllipsisV, faMinusCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { differenceInDays } from "date-fns";

import AppContext from "~/common/AppContext";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { zindexRadixPopper } from "~/css/zindexes";
import { useDebouncedValue, useMedia } from "~/lib/hooks";
import useEffectAfterMount from "~/lib/hooks/useEffectAfterMount";
import { inCalendarMode, isMobile, useNewest } from "~/lib/utils";
import { type FiltersState, type nonKeywords, useEventFilters } from "~/settings/EventFilters";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import Tooltip from "~/ui/Tooltip";
import DismissibleTooltip from "~/ui/Tooltip/DismissibleTooltip";

import type { ViewMode } from "./utils";

import type { MeetingsPanelMenu_viewer$key } from "~/__generated__/MeetingsPanelMenu_viewer.graphql";

import styles from "./MeetingsPanel.module.css";

const EVENT_FILTER_MENU_TOOLTIP_KEY = "EventFiltersMenu__tooltip";

const viewerFragment = graphql`
	fragment MeetingsPanelMenu_viewer on Viewer {
		uxSimplificationEnabled
		...EventFilters_viewer
	}
`;

interface Props {
	viewer: MeetingsPanelMenu_viewer$key;
	onChange: () => void;
	panelOpen: boolean;
	viewMode: ViewMode;
	changeViewMode: (viewMode: string) => void;
	disabled?: boolean;
}

export default function MeetingsPanelMenu({
	viewer: viewerKey,
	onChange,
	panelOpen,
	viewMode,
	changeViewMode,
	disabled,
}: Props) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const appContext = useContext(AppContext);
	const { eventFilterOptions, updateFilters, filters: _filters, user } = useEventFilters(viewer);
	const [uiState, setUIState] = React.useState<FiltersState>(_filters);
	const filters = useNewest(_filters, uiState);

	const keywordTooltipContent = React.useMemo(() => {
		const keywordsForDisplay = filters["filterKeywords"] ? filters["filterKeywords"].join(", ") : "";

		const content =
			keywordsForDisplay.length === 0 ? (
				"No current keyword filters"
			) : (
				<>
					<b>Current keyword filters: </b>
					<p>{keywordsForDisplay}</p>
				</>
			);
		return <div className={styles.keywordTooltip}>{content}</div>;
	}, [filters]);

	const onCheckboxChange = React.useCallback(
		(filterName: nonKeywords) => (checked: boolean) => {
			setUIState({ ...filters, [filterName]: checked });
		},
		[filters],
	);

	const debouncedFilters = useDebouncedValue(uiState, 500);
	useEffectAfterMount(() => {
		const update = async () => {
			await updateFilters(debouncedFilters);
			onChange?.();
		};
		update();
	}, [debouncedFilters, onChange, updateFilters]);

	useEffect(() => onChange?.(), [_filters, onChange]);

	const { hasDismissed, dismiss } = React.useContext(DismissibleElementsContext);
	const dismissFiltersTooltip = useCallback(() => {
		dismiss(EVENT_FILTER_MENU_TOOLTIP_KEY);
	}, [dismiss]);

	const navIsInMobileMode = useMedia("(max-width: 735px)");

	const showFiltersTooltip = React.useMemo(
		() =>
			!navIsInMobileMode &&
			panelOpen &&
			!hasDismissed(EVENT_FILTER_MENU_TOOLTIP_KEY) &&
			!!user.internalAttributes?.dateConfigured &&
			differenceInDays(new Date(), new Date(user.internalAttributes.dateConfigured)) > 14,
		[hasDismissed, navIsInMobileMode, panelOpen, user.internalAttributes],
	);

	const onMenuOpenDismissTooltip = useCallback(() => {
		if (showFiltersTooltip) {
			dismissFiltersTooltip();
		}
	}, [dismissFiltersTooltip, showFiltersTooltip]);

	const openPreferences = useCallback(() => {
		appContext.openSettingsModal("preferences");
	}, [appContext]);

	return (
		<Menu mobileHeaderTitle="Meeting filters" onOpenChange={onMenuOpenDismissTooltip}>
			<Tooltip
				placement="bottom-end"
				popperOptions={{
					modifiers: [
						{
							name: "flip",
							options: {
								fallbackPlacements: ["bottom"],
							},
						},
						{
							name: "preventOverflow",
							options: {
								mainAxis: false,
							},
						},
					],
				}}
				visible={showFiltersTooltip}
				interactive
				content={
					<DismissibleTooltip
						text="Set filters to choose which events are visible to you in your Fellow calendar"
						icon={faCalendarDay}
						dismiss={dismissFiltersTooltip}
					/>
				}
			>
				<Menu.Trigger>
					<IconButton
						title="Meeting filters"
						icon={faEllipsisV}
						className={cx(styles.panelButton, styles.cornerButton)}
						disabled={disabled}
					/>
				</Menu.Trigger>
			</Tooltip>
			<Menu.Content>
				<Menu.RadioGroup value={viewMode} onValueChange={changeViewMode}>
					<Menu.RadioItem value="day">Day</Menu.RadioItem>
					<Menu.RadioItem value="agenda">Agenda</Menu.RadioItem>
				</Menu.RadioGroup>
				<Menu.Separator />
				<Menu.Sub mobileHeaderTitle="Show…">
					<Menu.SubTrigger>View settings</Menu.SubTrigger>
					<Menu.SubContent>
						{!isMobile && <Menu.Label>Show…</Menu.Label>}
						{eventFilterOptions.map(option => (
							<Menu.CheckboxItem
								key={option.name}
								checked={filters[option.name]}
								onCheckedChange={onCheckboxChange(option.name)}
							>
								{`${option.displayText.slice(0, 1).toUpperCase()}${option.displayText.slice(1)}`}
							</Menu.CheckboxItem>
						))}
					</Menu.SubContent>
				</Menu.Sub>
				{(!viewer.uxSimplificationEnabled || !inCalendarMode) && (
					<>
						<Menu.Separator />
						<Menu.Item icon={faMinusCircle} onSelect={openPreferences}>
							<Tooltip content={keywordTooltipContent} zIndex={zindexRadixPopper + 1} maxWidth={220}>
								<span>Hide by keyword…</span>
							</Tooltip>
						</Menu.Item>
						<Menu.Item icon={faCog} onSelect={openPreferences}>
							Open calendar settings
						</Menu.Item>
					</>
				)}
			</Menu.Content>
		</Menu>
	);
}
